import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAllClients,
  getAllRegionsForClient,
  getTeamsForRegion,
  getUsersForTeam,
  readAdminAnalyticsSelection,
  writeAdminAnalyticsSelection,
} from '../utils/managerAccess';

function selectionFromParams(searchParams) {
  return {
    client: searchParams.get('client') || '',
    region: searchParams.get('region') || '',
    team: searchParams.get('team') || '',
    user: searchParams.get('user') || '',
  };
}

function applySelectionToParams(base, selection) {
  const next = new URLSearchParams(base);
  ['client', 'region', 'team', 'user'].forEach((key) => {
    if (selection[key]) next.set(key, selection[key]);
    else next.delete(key);
  });
  return next;
}

const EMPTY_HIERARCHY = {
  loading: false,
  error: '',
  clients: [],
  regions: [],
  teams: [],
  users: [],
  hasClientScope: true,
  clientId: '',
  regionId: '',
  teamId: '',
  userId: '',
  selectedClient: null,
  selectedRegion: null,
  selectedTeam: null,
  selectedUser: null,
  showClientPicker: false,
  showRegionPicker: false,
  showTeamPicker: false,
  showUserPicker: false,
  setClient: () => {},
  setRegion: () => {},
  setTeam: () => {},
  setUser: () => {},
};

export function useAdminAnalyticsHierarchy({ enabled = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('client') || '';
  const regionId = searchParams.get('region') || '';
  const teamId = searchParams.get('team') || '';
  const userId = searchParams.get('user') || '';

  const [clients, setClients] = useState([]);
  const [regions, setRegions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');

  const selectionRef = useRef({ clientId, regionId, teamId, userId });
  selectionRef.current = { clientId, regionId, teamId, userId };

  const commitSelection = useCallback((nextSelection) => {
    writeAdminAnalyticsSelection(nextSelection);
    setSearchParams((prev) => applySelectionToParams(prev, nextSelection), { replace: true });
  }, [setSearchParams]);

  const setClient = useCallback((id) => {
    commitSelection({ client: id || '', region: '', team: '', user: '' });
  }, [commitSelection]);

  const setRegion = useCallback((id) => {
    const { clientId: currentClientId } = selectionRef.current;
    commitSelection({ client: currentClientId, region: id || '', team: '', user: '' });
  }, [commitSelection]);

  const setTeam = useCallback((id) => {
    const { clientId: currentClientId, regionId: currentRegionId } = selectionRef.current;
    commitSelection({ client: currentClientId, region: currentRegionId, team: id || '', user: '' });
  }, [commitSelection]);

  const setUser = useCallback((id) => {
    const { clientId: currentClientId, regionId: currentRegionId, teamId: currentTeamId } = selectionRef.current;
    commitSelection({
      client: currentClientId,
      region: currentRegionId,
      team: currentTeamId,
      user: id || '',
    });
  }, [commitSelection]);

  useEffect(() => {
    if (!enabled) return;
    const stored = readAdminAnalyticsSelection();
    const fromUrl = selectionFromParams(searchParams);
    const hasUrl = fromUrl.client || fromUrl.region || fromUrl.team || fromUrl.user;
    if (!hasUrl && stored.client) {
      setSearchParams((prev) => applySelectionToParams(prev, stored), { replace: true });
    }
  }, [enabled, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) return undefined;

    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await getAllClients();
        if (!mounted) return;
        setClients(list);
      } catch {
        if (mounted) setError('Failed to load clients');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !clientId) {
      setRegions([]);
      return undefined;
    }

    let mounted = true;
    getAllRegionsForClient(clientId).then(({ regions: regionList }) => {
      if (!mounted) return;
      setRegions(regionList);
    });
    return () => { mounted = false; };
  }, [enabled, clientId]);

  useEffect(() => {
    if (!enabled || !clientId || regionId || regions.length !== 1) return;
    commitSelection({ client: clientId, region: String(regions[0].id), team: '', user: '' });
  }, [enabled, clientId, regionId, regions, commitSelection]);

  useEffect(() => {
    if (!enabled || !clientId || !regionId) {
      setTeams([]);
      return undefined;
    }

    let mounted = true;
    getTeamsForRegion(clientId, regionId).then((teamList) => {
      if (!mounted) return;
      setTeams(teamList);
    });
    return () => { mounted = false; };
  }, [enabled, clientId, regionId]);

  useEffect(() => {
    if (!enabled || !clientId || !regionId || teamId || teams.length !== 1) return;
    commitSelection({ client: clientId, region: regionId, team: String(teams[0].id), user: '' });
  }, [enabled, clientId, regionId, teamId, teams, commitSelection]);

  useEffect(() => {
    if (!enabled || !teamId) {
      setUsers([]);
      return undefined;
    }

    let mounted = true;
    getUsersForTeam(teamId).then((userList) => {
      if (!mounted) return;
      setUsers(userList);
    });
    return () => { mounted = false; };
  }, [enabled, teamId]);

  useEffect(() => {
    if (!enabled || !clientId || !regionId || !teamId || userId || users.length !== 1) return;
    commitSelection({
      client: clientId,
      region: regionId,
      team: teamId,
      user: String(users[0].id),
    });
  }, [enabled, clientId, regionId, teamId, userId, users, commitSelection]);

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.id) === String(clientId)) || null,
    [clients, clientId]
  );
  const selectedRegion = useMemo(
    () => regions.find((r) => String(r.id) === String(regionId)) || null,
    [regions, regionId]
  );
  const selectedTeam = useMemo(
    () => teams.find((t) => String(t.id) === String(teamId)) || null,
    [teams, teamId]
  );
  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(userId)) || null,
    [users, userId]
  );

  if (!enabled) {
    return EMPTY_HIERARCHY;
  }

  return {
    loading,
    error,
    clients,
    regions,
    teams,
    users,
    hasClientScope: true,
    clientId,
    regionId,
    teamId,
    userId,
    selectedClient,
    selectedRegion,
    selectedTeam,
    selectedUser,
    showClientPicker: clients.length > 1,
    showRegionPicker: regions.length > 1,
    showTeamPicker: teams.length > 1,
    showUserPicker: users.length > 1,
    setClient,
    setRegion,
    setTeam,
    setUser,
  };
}
